<template>
  <dialog
    ref="dialogEl"
    class="scrim"
    aria-labelledby="banlist-reward-title"
    @cancel.prevent="onLater"
    @click="onScrimClick"
  >
    <div class="panel">
      <div class="head">
        <div>
          <p class="kicker">{{ t('banlistReward.kicker') }}</p>
          <h2 id="banlist-reward-title">{{ t('banlistReward.title') }}</h2>
        </div>
        <CloseXButton :label="t('banlistReward.later')" @click="onLater" />
      </div>

      <p class="lead">{{ lead }}</p>
      <p v-if="error" class="err">{{ error }}</p>

      <div v-if="confirming && selected" class="confirm">
        <span class="confirm-frame">
          <CardArt
            class="art"
            :card-id="selected.id"
            intensity="thumb"
            :alt="selected.name"
          />
        </span>
        <p class="confirm-name">{{ t('banlistReward.confirm', { name: selected.name }) }}</p>
        <p class="confirm-body">{{ t('banlistReward.confirmBody') }}</p>
        <p class="meta">
          {{ t('banlistReward.max', { n: selected.max }) }}
          · {{ t('banlistReward.owned', { n: selected.owned }) }}
        </p>
        <div class="actions">
          <button type="button" class="text" :disabled="busy" @click="confirming = false">
            {{ t('banlistReward.back') }}
          </button>
          <button type="button" class="cta" :disabled="busy" @click="onAdd">
            {{ busy ? t('banlistReward.adding') : t('banlistReward.add') }}
          </button>
        </div>
      </div>

      <div v-else class="list">
        <p v-if="groups.length === 0" class="empty">{{ t('banlistReward.empty') }}</p>
        <section v-for="group in groups" :key="group.max" class="group">
          <h3>{{ t(group.max === 1 ? 'banlist.limited1' : 'banlist.limited2') }}</h3>
          <ul>
            <li v-for="row in group.cards" :key="row.id">
              <button type="button" class="card-row" @click="onPick(row)">
                <span class="art-frame">
                  <CardArt
                    class="art"
                    :card-id="row.id"
                    intensity="thumb"
                    :alt="row.name"
                  />
                </span>
                <span class="copy">
                  <span class="name">{{ row.name }}</span>
                  <span class="meta">
                    {{ t('banlistReward.max', { n: row.max }) }}
                    · {{ t('banlistReward.owned', { n: row.owned }) }}
                  </span>
                </span>
              </button>
            </li>
          </ul>
        </section>
      </div>

      <div v-if="!confirming" class="actions foot">
        <button type="button" class="text" @click="onLater">
          {{ t('banlistReward.later') }}
        </button>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { BanlistLevelReward, CollectionState } from '@shared/index'
import { api } from '../api/http'
import { useI18n } from '../i18n/t'
import { localizedName } from '../i18n/localizeCard'
import { useAuthStore } from '../stores/auth'
import { useBanlistRewardStore } from '../stores/banlistReward'
import { useCollectionStore } from '../stores/collection'
import { useSiteStore } from '../stores/site'
import CardArt from './CardArt.vue'
import CloseXButton from './CloseXButton.vue'

const emit = defineEmits<{ later: [] }>()

const { t } = useI18n()
const auth = useAuthStore()
const reward = useBanlistRewardStore()
const collection = useCollectionStore()
const site = useSiteStore()

const dialogEl = ref<HTMLDialogElement | null>(null)
const busy = ref(false)
const error = ref('')
const confirming = ref(false)
const selected = ref<{ id: string; name: string; max: number; owned: number } | null>(null)

const lead = computed(() =>
  reward.available <= 1
    ? t('banlistReward.leadOne')
    : t('banlistReward.lead', { n: reward.available }),
)

const groups = computed(() => {
  const byMax = new Map<number, { id: string; max: number; name: string; owned: number }[]>()
  for (const [id, max] of Object.entries(site.houseLimits)) {
    if (max !== 1 && max !== 2) continue
    const list = byMax.get(max) ?? []
    list.push({
      id,
      max,
      name: localizedName(id),
      owned: collection.ownedCount(id),
    })
    byMax.set(max, list)
  }
  return [...byMax.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([max, cards]) => ({
      max,
      cards: cards.sort((a, b) => a.name.localeCompare(b.name)),
    }))
})

onMounted(() => {
  dialogEl.value?.showModal()
})

function onLater(): void {
  if (busy.value) return
  emit('later')
}

function onScrimClick(event: MouseEvent): void {
  if (event.target === dialogEl.value) onLater()
}

function onPick(row: { id: string; name: string; max: number; owned: number }): void {
  if (busy.value) return
  error.value = ''
  selected.value = row
  confirming.value = true
}

async function onAdd(): Promise<void> {
  const card = selected.value
  const token = auth.token
  if (!card || !token || busy.value) return
  busy.value = true
  error.value = ''
  try {
    const payload = await api<{
      collection: CollectionState
      banlistReward: BanlistLevelReward
    }>('/collection/banlist-reward', {
      method: 'POST',
      token,
      body: JSON.stringify({ passcode: card.id }),
    })
    collection.applyState(payload.collection)
    reward.apply(payload.banlistReward)
    selected.value = null
    confirming.value = false
    if (payload.banlistReward.available < 1) reward.closeDialog()
  } catch {
    error.value = t('banlistReward.error')
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.scrim {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  border: 0;
  background: transparent;
  color: var(--md-sys-color-on-surface);
  overflow: hidden;
  font-family: Roboto, system-ui, sans-serif;
}

.scrim::backdrop {
  background: var(--md-scrim);
  backdrop-filter: blur(10px);
}

.panel {
  display: flex;
  flex-direction: column;
  width: min(560px, 100%);
  max-height: min(80vh, 720px);
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  border: 1px solid color-mix(in srgb, var(--md-sys-color-primary) 40%, transparent);
  box-shadow: var(--md-elevation-2);
  color: var(--md-sys-color-on-surface);
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 16px 0 24px;
}

.kicker {
  margin: 0 0 4px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 22px;
  line-height: 28px;
  font-weight: 400;
}

.lead,
.err,
.empty,
.confirm-body,
.meta {
  margin: 8px 24px 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.err {
  color: var(--md-sys-color-error);
}

.list {
  overflow: auto;
  margin-top: 12px;
  padding: 0 12px 8px;
}

.group {
  margin: 0 0 12px;
}

h3 {
  margin: 8px 12px;
  font-size: 14px;
  font-weight: 500;
}

ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.card-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 0 0 6px;
  padding: 8px;
  border: 0;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container-high);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.card-row:hover {
  background: color-mix(in srgb, var(--md-sys-color-primary) 12%, var(--md-sys-color-surface-container-high));
}

.art-frame {
  width: 48px;
  height: 70px;
  flex: none;
}

.confirm-frame {
  width: 96px;
  height: 140px;
  margin: 16px 24px 0;
}

.art {
  width: 100%;
  height: 100%;
}

.copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.name,
.confirm-name {
  font-size: 16px;
  line-height: 22px;
}

.card-row .meta {
  margin: 0;
}

.confirm {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: 8px;
}

.confirm-name {
  margin: 12px 24px 0;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 16px 16px 24px;
}

.actions.foot {
  padding-top: 8px;
}

.text,
.cta {
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.text {
  background: transparent;
  color: var(--md-sys-color-primary);
}

.cta {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.text:disabled,
.cta:disabled {
  opacity: 0.45;
  cursor: default;
}

@media (prefers-reduced-motion: reduce) {
  .scrim,
  .card-row {
    animation: none;
    transition: none;
  }
}
</style>
